// ─────────────────────────────────────────────────────────────────────────────
// FEATURE: Admin sign-in  (username + password, and fingerprint via WebAuthn passkeys)
// Staff accounts are ordinary Firebase Auth users with a `role` custom claim (admin / editor / viewer, set only by the backend or
// backend/scripts/create_staff.py). A USERNAME is turned into a private, non-mailable e-mail (`<username>@staff.zenfresh.invalid`, the
// reserved .invalid TLD so a password-reset mail can never reach a stranger); typing a real e-mail address also works.
// FINGERPRINT: after signing in with a password, a staff member ENROLS a fingerprint (a platform authenticator — Touch ID, Windows Hello,
// Android biometrics; user verification required). Later "Sign in with fingerprint" needs no username: the browser offers the discoverable
// credential, the backend verifies it (utils → /api/staff/passkeys/*) and returns a Firebase custom token for the existing account.
// The private key never leaves the device. Neighbours: components/AuthProvider (session + role), pages/StaffLogin, pages/admin/Security.
// ─────────────────────────────────────────────────────────────────────────────
import type { StaffRole } from "./authContext";
import { ApiError, apiAuthed, apiPost } from "./api";

export const STAFF_EMAIL_DOMAIN = "staff.zenfresh.invalid";

/** "asha" → "asha@staff.zenfresh.invalid"; anything containing "@" is used as typed. */
export const staffEmail = (input: string) => {
  const value = input.trim().toLowerCase();
  return value.includes("@") ? value : `${value}@${STAFF_EMAIL_DOMAIN}`;
};

export const displayName = (email: string | null) => (email ? email.replace(`@${STAFF_EMAIL_DOMAIN}`, "") : "staff");

export const isStaffRole = (value: unknown): value is StaffRole => value === "admin" || value === "editor" || value === "viewer";

export const passkeysSupported = () => typeof window !== "undefined" && "PublicKeyCredential" in window && typeof navigator.credentials?.create === "function";

// ── base64url ⇄ bytes (WebAuthn JSON uses base64url) ─────────────────────────
const fromB64 = (value: string): ArrayBuffer => {
  const base64 = value.replace(/-/g, "+").replace(/_/g, "/").padEnd(Math.ceil(value.length / 4) * 4, "=");
  const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
  return bytes.buffer;
};
const toB64 = (buffer: ArrayBuffer | null): string | undefined => {
  if (!buffer) return undefined;
  let text = "";
  for (const byte of new Uint8Array(buffer)) text += String.fromCharCode(byte);
  return btoa(text).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
};

interface ChallengeResponse {
  challengeId: string;
  options: Record<string, unknown>;
}

// Options come as JSON (base64url strings); the browser wants ArrayBuffers.
function creationOptions(json: Record<string, unknown>): PublicKeyCredentialCreationOptions {
  const o = json as unknown as {
    challenge: string;
    user: { id: string; name: string; displayName: string };
    excludeCredentials?: { id: string; type: "public-key" }[];
  } & Record<string, unknown>;
  return {
    ...(o as object),
    challenge: fromB64(o.challenge),
    user: { ...o.user, id: fromB64(o.user.id) },
    excludeCredentials: (o.excludeCredentials ?? []).map((c) => ({ ...c, id: fromB64(c.id) })),
  } as PublicKeyCredentialCreationOptions;
}

function requestOptions(json: Record<string, unknown>): PublicKeyCredentialRequestOptions {
  const o = json as unknown as { challenge: string; allowCredentials?: { id: string; type: "public-key" }[] } & Record<string, unknown>;
  return {
    ...(o as object),
    challenge: fromB64(o.challenge),
    allowCredentials: (o.allowCredentials ?? []).map((c) => ({ ...c, id: fromB64(c.id) })),
  } as PublicKeyCredentialRequestOptions;
}

/** Turns a browser's "the person cancelled / no fingerprint here" into a friendly message. */
function friendly(error: unknown, fallback: string): Error {
  if (error instanceof ApiError) return error;
  if (error instanceof DOMException) {
    if (error.name === "NotAllowedError") return new Error("The fingerprint prompt was cancelled or timed out.");
    if (error.name === "InvalidStateError") return new Error("This device already has a fingerprint registered for your account.");
    if (error.name === "NotSupportedError") return new Error("This device can't create a fingerprint sign-in.");
  }
  return new Error(fallback);
}

// ── Fingerprint sign-in (public) ─────────────────────────────────────────────
/** Ask the browser for a fingerprint, verify it with the backend, return a Firebase custom token. */
export async function fingerprintCustomToken(): Promise<string> {
  const begun = await apiPost<ChallengeResponse>("/staff/passkeys/login/options", {});
  let credential: PublicKeyCredential;
  try {
    credential = (await navigator.credentials.get({ publicKey: requestOptions(begun.options) })) as PublicKeyCredential;
  } catch (error) {
    throw friendly(error, "We couldn't read your fingerprint.");
  }
  const response = credential.response as AuthenticatorAssertionResponse;
  const done = await apiPost<{ customToken: string }>("/staff/passkeys/login/verify", {
    challengeId: begun.challengeId,
    credential: {
      id: credential.id,
      rawId: toB64(credential.rawId),
      type: credential.type,
      response: {
        authenticatorData: toB64(response.authenticatorData),
        clientDataJSON: toB64(response.clientDataJSON),
        signature: toB64(response.signature),
        userHandle: toB64(response.userHandle),
      },
      clientExtensionResults: credential.getClientExtensionResults(),
    },
  });
  return done.customToken;
}

// ── Fingerprint enrolment (signed in) ────────────────────────────────────────
export interface Passkey {
  id: string;
  label: string;
  createdAt: string;
  lastUsedAt: string | null;
}

export const fetchPasskeys = () => apiAuthed<Passkey[]>("GET", "/staff/passkeys/");
export const deletePasskey = (id: string) => apiAuthed<{ deleted: boolean }>("DELETE", `/staff/passkeys/${id}`);

/** Register this device's fingerprint for the signed-in staff member. */
export async function enrolFingerprint(label: string): Promise<Passkey> {
  const begun = await apiAuthed<ChallengeResponse>("POST", "/staff/passkeys/register/options");
  let credential: PublicKeyCredential;
  try {
    credential = (await navigator.credentials.create({ publicKey: creationOptions(begun.options) })) as PublicKeyCredential;
  } catch (error) {
    throw friendly(error, "We couldn't register your fingerprint.");
  }
  const response = credential.response as AuthenticatorAttestationResponse;
  return apiAuthed<Passkey>("POST", "/staff/passkeys/register/verify", {
    challengeId: begun.challengeId,
    label,
    credential: {
      id: credential.id,
      rawId: toB64(credential.rawId),
      type: credential.type,
      response: {
        attestationObject: toB64(response.attestationObject),
        clientDataJSON: toB64(response.clientDataJSON),
        transports: response.getTransports?.() ?? [],
      },
      clientExtensionResults: credential.getClientExtensionResults(),
    },
  });
}
